import { Button, Checkbox, Flex, Form } from "antd";
import { type ReactNode, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import Page from "~Common/Page";
import PermissionCheck from "~Common/PermissionCheck";
import { type PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { ManagePageTitle } from "./ManagePageTitle";
import "./ManagePage.scss";

type Props<TForm extends object> = {
  entityType: string;
  permissionNames: Array<PermissionNames>;
  entityName?: string;
  children: ReactNode;
  isLoading: boolean;
  isSaving: boolean;
  onBack?: () => void;
  onSubmit: (values: TForm) => Promise<unknown>;
  onFinalCreateSuccess?: (values: TForm) => void;
  initialValues?: Partial<TForm>;
};

const ManagePage = <TForm extends object>({
  entityType,
  permissionNames,
  entityName,
  onBack,
  onSubmit,
  initialValues,
  isLoading,
  isSaving,
  onFinalCreateSuccess,
  children,
}: Props<TForm>) => {
  const navigate = useNavigate();
  const [resetAfterSubmit, setResetAfterSubmit] = useState(false);
  const [form] = Form.useForm<TForm>();
  const isEditPage = !!entityName;

  const handleCancel = () => {
    if (onBack) {
      onBack();
      return;
    }

    navigate(-1);
  };

  const handleSubmit = (values: TForm) =>
    onSubmit(values).then(() => {
      if (resetAfterSubmit) {
        form.resetFields();
        setResetAfterSubmit(false);
      } else if (!isEditPage) {
        onFinalCreateSuccess?.(values);
      }
    });

  return (
    <Page title={<ManagePageTitle entityType={entityType} entityName={entityName} isLoading={isLoading} />} onBack={onBack}>
      <Form<TForm>
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={handleSubmit}
        key={isEditPage ? "edit-form" : "create-form"}
      >
        <Flex vertical gap="large">
          <div>{children}</div>
          {!isEditPage && !isLoading && (
            <Checkbox checked={resetAfterSubmit} onChange={(e) => setResetAfterSubmit(e.target.checked)}>
              <FormattedMessage id="add.another.item" values={{ item: <FormattedMessage id={entityType} /> }} />
            </Checkbox>
          )}
          {!isLoading && (
            <Flex gap="small">
              <PermissionCheck
                permissionKeys={permissionNames}
                permissionOptions={isEditPage ? [PermissionOperations.UPDATE] : [PermissionOperations.CREATE]}
              >
                <Button loading={isSaving} disabled={isSaving} type="primary" htmlType="submit" className="manage-page-save-button">
                  <FormattedMessage
                    id={`managePage.${isEditPage ? "edit" : "add"}.save.item.button`}
                    values={{ item: <FormattedMessage id={entityType} /> }}
                  />
                </Button>
              </PermissionCheck>
              <Button disabled={isSaving} htmlType="button" onClick={handleCancel} className="manage-page-cancel-button">
                <FormattedMessage id={isEditPage ? "managePage.button.cancel" : "managePage.button.discard"} />
              </Button>
            </Flex>
          )}
        </Flex>
      </Form>
    </Page>
  );
};

export default ManagePage;
